import { forwardRef } from 'react';
import classNames from 'classnames';
import { SpacingType } from '@vds/core';
import './Container.scss';

export interface ContainerProps {
  component?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
  styles?: React.CSSProperties;
  fluid?: boolean;
  flexDirection?: React.CSSProperties['flexDirection'];
  alignItems?: React.CSSProperties['alignItems'];
  justifyContent?: React.CSSProperties['justifyContent'];
  maxWidth?: React.CSSProperties['maxWidth'];
  gap?: SpacingType;
}

/**
 * Box Component
 * @param styles - The system prop that allows defining system overrides as well as additional CSS styles.
 * @param component - The component used for the root node. Either a string to use a HTML element or a component.
 * @param className - Additional classNames for the component.
 * @param children - Any React.Node Element.
 * @param justifyContent - Centers the content horizontally.
 * @param alignItems - Centers the content vertically.
 * @param flexDirection - Sets the direction of children.
 * @param fluid - If true, it will have full width.
 * @param maxWidth - Sets the max width of the container.
 * @param gap - If set, children will be have a gutter space each other. This prop uses tokens.
 */
export const Container = forwardRef<React.ReactNode, ContainerProps>(
  (
    {
      component: Component = 'div',
      className,
      children,
      styles = {},
      fluid,
      flexDirection,
      alignItems,
      justifyContent,
      maxWidth,
      gap,
      ...rest
    },
    ref,
  ) => {
    return (
      <Component
        className={classNames(
          'vds-container',
          {
            'vds-container--fluid': fluid,
            [`vds-container--gap-${gap}`]: gap,
          },
          className,
        )}
        ref={ref}
        style={{ ...styles, flexDirection, alignItems, justifyContent, maxWidth }}
        {...rest}>
        {children}
      </Component>
    );
  },
);
